import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { EnhancedSeoHead } from "@appcaire/shared/seo";
import { FAQ } from "@appcaire/shared/seo";
import { CTABox } from "@appcaire/shared/seo";
import {
  generateArticleSchema,
  generateFAQSchema,
  generateBreadcrumbSchema,
  generateOrganizationSchema,
  generateWebSiteSchema,
  generateLocalBusinessSchema,
  combineSchemas,
} from "@appcaire/shared/seo";
import {
  Search,
  FileText,
  Shield,
  Users,
  Phone,
  HeartIcon,
  CheckCircle,
  ArrowRight,
  HelpCircle,
  Star,
} from "lucide-react";

/**
 * Static data hoisted outside component (React best practice: rendering-hoist-jsx)
 * These arrays don't change, so they don't need to be recreated on every render
 */
const QUICK_LINKS = [
  {
    title: "Jämför anordnare",
    description:
      "Lista över godkända hemtjänstutförare i Nacka – jämför och välj.",
    to: "/utforare",
    icon: Users,
    color: "bg-amber-50 text-amber-700 border-amber-200",
  },
  {
    title: "Hitta rätt hemtjänst",
    description: "Guide till hur du hittar och väljer hemtjänst i Nacka.",
    to: "/hitta-hemtjanst",
    icon: Search,
    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  {
    title: "Ansök om hemtjänst",
    description: "Steg-för-steg guide för att ansöka hos Nacka kommun.",
    to: "/ansoka",
    icon: FileText,
    color: "bg-blue-50 text-blue-700 border-blue-200",
  },
  {
    title: "Dina rättigheter",
    description:
      "Vad du har rätt till enligt lag och vad hemtjänsten kan hjälpa med.",
    to: "/rattigheter",
    icon: Shield,
    color: "bg-purple-50 text-purple-700 border-purple-200",
  },
  {
    title: "Nya socialtjänstlagen",
    description: "Vad den nya lagen betyder för hemtjänst och stöd i Nacka.",
    to: "/nya-socialtjanstlagen-hemtjanst",
    icon: FileText,
    color: "bg-amber-50 text-amber-700 border-amber-200",
  },
  {
    title: "Recensioner och omdömen",
    description:
      "Så läser du brukarundersökningen och jämför utförare på samma mått.",
    to: "/recensioner",
    icon: Star,
    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  {
    title: "Hushållsnära tjänster",
    description:
      "Städning, tvätt och annan hjälp du kan köpa utöver ditt biståndsbeslut.",
    to: "/hushallsnara-tjanster",
    icon: HeartIcon,
    color: "bg-blue-50 text-blue-700 border-blue-200",
  },
  {
    title: "Vanliga frågor",
    description: "Svar om ansökan, avgifter, byte av utförare och mer.",
    to: "/vanliga-fragor-och-svar",
    icon: HelpCircle,
    color: "bg-purple-50 text-purple-700 border-purple-200",
  },
];

const FAQ_ITEMS = [
  {
    question: "Hur ansöker jag om hemtjänst i Nacka?",
    answer:
      "Du ansöker genom att kontakta Nacka kommuns biståndshandläggare. Ring 08-718 80 00 eller besök Nacka kommuns hemsida. En handläggare kommer att bedöma dina behov och besluta om vilken hjälp du har rätt till.",
  },
  {
    question: "Kan jag välja vilken hemtjänst jag vill ha?",
    answer:
      "Ja! Nacka har ett kundvalssystem som innebär att du själv väljer vilken anordnare som ska hjälpa dig. Du kan byta anordnare när du vill om du inte är nöjd.",
  },
  {
    question: "Vad kostar hemtjänst i Nacka?",
    answer:
      "Kostnaden beror på din inkomst och hur mycket hjälp du behöver. Det finns en maxtaxa som begränsar hur mycket du maximalt kan behöva betala. Kontakta kommunen för en personlig beräkning.",
  },
  {
    question: "Vad kan hemtjänsten hjälpa mig med?",
    answer:
      "Hemtjänsten kan hjälpa med personlig omvårdnad (dusch, påklädning), städning, tvätt, matlagning, inköp, promenader, socialt umgänge och mycket mer. Vilken hjälp du får beror på ditt biståndsbeslut.",
  },
  {
    question: "Hur snabbt kan jag få hemtjänst?",
    answer:
      "I akuta fall kan hjälpen starta redan samma dag. Normalt tar det några dagar till ett par veckor från ansökan till att hjälpen börjar, beroende på hur snabbt utredningen kan göras.",
  },
];

/**
 * Home - nackahemtjanst.se startsida
 *
 * Consumer-focused landing page for Nacka residents seeking home care.
 * SEO Focus: "hemtjänst Nacka", "hitta hemtjänst Nacka"
 * Target: Brukare and anhöriga (elderly and family members)
 */
const Home: React.FC = () => {
  /**
   * Gets the correct absolute path for links (always root-level for standalone app)
   */
  const getPath = (path: string) => path;

  // Memoize schema generation (React best practice: rerender-memo)
  // Schema objects don't change between renders, so memoize them
  const articleSchema = useMemo(
    () =>
      generateArticleSchema({
        headline: "Hemtjänst i Nacka – Din guide till omsorg och stöd",
        description:
          "Komplett guide för dig som söker hemtjänst i Nacka kommun. Jämför utförare, ansök om hjälp och förstå dina rättigheter.",
        url: "https://nackahemtjanst.se/",
        datePublished: "2025-12-22",
        dateModified: "2026-06-29",
      }),
    [],
  );

  const faqSchema = useMemo(
    () =>
      generateFAQSchema(
        FAQ_ITEMS.map((item) => ({
          question: item.question,
          answer: item.answer,
        })),
        "https://nackahemtjanst.se/",
      ),
    [],
  );

  const breadcrumbSchema = useMemo(
    () =>
      generateBreadcrumbSchema([
        { name: "Hem", url: "https://nackahemtjanst.se/" },
      ]),
    [],
  );

  const organizationSchema = useMemo(
    () =>
      generateOrganizationSchema({
        name: "Nacka Hemtjänst",
        url: "https://nackahemtjanst.se",
        logo: "https://nackahemtjanst.se/favicons/favicon-nackahemtjanst-v2-512x512.png",
        description:
          "Lokal guide till hemtjänst i Nacka kommun. Vi hjälper brukare och anhöriga att navigera kundval och hitta rätt anordnare.",
      }),
    [],
  );

  const websiteSchema = useMemo(
    () =>
      generateWebSiteSchema({
        name: "Nacka Hemtjänst",
        url: "https://nackahemtjanst.se",
        description: "Din guide till hemtjänst i Nacka kommun",
      }),
    [],
  );

  const localBusinessSchema = useMemo(
    () =>
      generateLocalBusinessSchema({
        name: "Nacka kommun - Hemtjänst",
        description: "Nacka kommuns hemtjänstservice",
        url: "https://www.nacka.se",
        phone: "+46-8-718-80-00",
        city: "Nacka",
        address: "Nacka kommun",
      }),
    [],
  );

  const finalCombinedSchema = useMemo(
    () =>
      combineSchemas([
        articleSchema,
        faqSchema,
        breadcrumbSchema,
        organizationSchema,
        websiteSchema,
        localBusinessSchema,
      ]),
    [
      articleSchema,
      faqSchema,
      breadcrumbSchema,
      organizationSchema,
      websiteSchema,
      localBusinessSchema,
    ],
  );

  return (
    <>
      <EnhancedSeoHead
        title="Hemtjänst i Nacka – Din guide till omsorg och stöd"
        description="Komplett guide för dig som söker hemtjänst i Nacka kommun. Jämför utförare, ansök om hjälp och förstå dina rättigheter."
        canonicalUrl="https://nackahemtjanst.se/"
        ogImage="https://nackahemtjanst.se/og-image.png"
        ogType="article"
        keywords={[
          "hemtjänst Nacka",
          "hemtjänst Nacka kommun",
          "ansöka hemtjänst Nacka",
          "hemtjänstutförare Nacka",
          "hemtjänst rättigheter Nacka",
          "kundval Nacka",
          "hemtjänst Nacka guide",
          "hitta hemtjänst Nacka",
          "hemtjänst Nacka kontakt",
          "hemtjänst Nacka anordnare",
          "Nacka hemtjänst",
          "hemtjänst Nacka ansökan",
          "hemtjänst Nacka information",
        ]}
        contentClassification="Consumer Portal"
        targetAudience="seniors, families"
        coverage="Sweden"
        publishDate="2025-12-22T00:00:00+01:00"
        modifiedDate="2026-06-29T00:00:00+01:00"
        author="Nacka Hemtjänst"
        schemaMarkup={finalCombinedSchema ?? undefined}
        entities={[{ type: "Place", name: "Nacka" }]}
      />

      {/* Hero Section with Glass Effect */}
      <section className="relative bg-gradient-to-b from-emerald-50 via-white to-white py-16 md:py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-100/20 via-transparent to-blue-100/20" />
        {/* Dotted pattern overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle, rgba(99, 102, 241, 0.15) 1.5px, transparent 1.5px)",
            backgroundSize: "24px 24px",
            zIndex: 1,
          }}
        />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 inline-block mb-6 shadow-glass-light">
            <HeartIcon className="w-16 h-16 text-emerald-600" />
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-surface-900 tracking-tight mb-6">
            Hemtjänst i Nacka
          </h1>
          <p className="text-xl md:text-2xl text-surface-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            Din oberoende guide till omsorg och stöd i hemmet. Vi hjälper dig
            hitta rätt hjälp, förstå dina rättigheter och navigera
            ansökningsprocessen.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={getPath("/utforare")}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-emerald-600 text-white text-lg font-semibold shadow-lg hover:bg-emerald-700 transition-all active:scale-[0.98]"
            >
              Jämför anordnare
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to={getPath("/ansoka")}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-glass-white backdrop-blur-glass border border-glass-border text-emerald-700 text-lg font-semibold hover:bg-glass-hover transition-all shadow-glass-light"
            >
              Ansök om hjälp
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Links Grid with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-surface-900 mb-12">
            Vad letar du efter?
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {QUICK_LINKS.map((link) => (
              <Link
                key={link.to}
                to={getPath(link.to)}
                className={`group p-6 rounded-2xl border-2 ${link.color} hover:shadow-lg transition-all bg-glass-white backdrop-blur-glass`}
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-white shadow-sm">
                    <link.icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2 group-hover:underline">
                      {link.title}
                    </h3>
                    <p className="text-surface-600">{link.description}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Nacka Kundval Info with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-surface-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-3xl shadow-glass-light p-8 md:p-12">
            <h2 className="text-3xl font-bold text-surface-900 mb-6">
              Så fungerar kundval i Nacka
            </h2>
            <p className="text-lg text-surface-600 mb-8 leading-relaxed">
              I Nacka har du som brukare rätt att själv välja vilken hemtjänst
              du vill ha. Det kallas kundval och innebär att makten ligger hos
              dig.
            </p>

            <div className="space-y-4">
              {[
                "Du väljer själv vilken anordnare som ska hjälpa dig",
                "Du kan byta anordnare när du vill – utan att behöva ange skäl",
                "Alla godkända anordnare uppfyller kommunens kvalitetskrav",
                "Ersättningen till utföraren är lika oavsett vem du väljer",
              ].map((point, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span className="text-lg text-surface-700">{point}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-8 border-t border-glass-border">
              <Link
                to={getPath("/utforare")}
                className="inline-flex items-center gap-2 text-emerald-600 font-semibold hover:text-emerald-700 text-lg"
              >
                Se alla godkända anordnare i Nacka
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-br from-emerald-700 to-emerald-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-700/95 to-emerald-600/95 backdrop-blur-sm" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="bg-glass-white/10 backdrop-blur-glass border border-white/20 rounded-2xl p-8 shadow-glass-light">
            <Phone className="w-12 h-12 mx-auto mb-6 opacity-80" />
            <h2 className="text-3xl font-bold mb-4">
              Behöver du hjälp att komma igång?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Nacka kommuns servicecenter hjälper dig med frågor om hemtjänst
              och kan guida dig genom ansökningsprocessen.
            </p>
            <a
              href="tel:0871880000"
              className="inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-white text-emerald-700 text-xl font-bold hover:bg-emerald-50 transition-colors shadow-lg"
            >
              <Phone className="w-6 h-6" />
              Ring 08-718 80 00
            </a>
            <p className="mt-4 text-white/80 text-sm">Vardagar 08:00–17:00</p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <FAQ items={FAQ_ITEMS} title="Vanliga frågor om hemtjänst i Nacka" />
        </div>
      </section>

      {/* Modern Systems CTA with Glass Effect */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto">
          <CTABox
            title="Tips: Välj en anordnare med moderna system"
            text="Hemtjänstföretag som använder moderna digitala verktyg kan ofta erbjuda bättre kontinuitet och tydligare kommunikation. Fråga efter om de har en app där du kan se ditt schema och kommunicera med personalen."
            buttonText="Läs mer om modern hemtjänst"
            buttonLink={getPath("/hitta-hemtjanst")}
            variant="glass"
          />
        </div>
      </section>

      {/* Disclaimer with Glass Effect */}
      <section className="py-12 px-4 bg-gradient-to-b from-surface-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-6 shadow-glass-light">
            <p className="text-sm text-surface-600 leading-relaxed">
              <strong>Viktig information:</strong> Denna webbplats är en
              oberoende guide och är inte en del av Nacka kommun. För officiell
              information, ansökan och tjänster, kontakta alltid{" "}
              <a
                href="https://www.nacka.se"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-600 font-medium hover:underline"
              >
                Nacka kommun
              </a>{" "}
              direkt.
            </p>
          </div>
        </div>
      </section>

      {/* Cross-link to hemtjanstguide.se */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-surface-50">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-glass-white backdrop-blur-glass border border-glass-border rounded-2xl p-8 shadow-glass-light">
            <h2 className="text-2xl font-bold text-surface-900 mb-4">
              Letar du efter hemtjänst i andra kommuner?
            </h2>
            <p className="text-surface-600 mb-6 max-w-2xl mx-auto">
              På{" "}
              <a
                href="https://hemtjanstguide.se"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-600 font-medium hover:underline"
              >
                hemtjanstguide.se
              </a>{" "}
              kan du hitta information om hemtjänst i alla Sveriges kommuner.
              Jämför anordnare, läs om rättigheter och få hjälp att hitta rätt
              omsorg.
            </p>
            <a
              href="https://hemtjanstguide.se"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-all shadow-lg"
            >
              Besök hemtjanstguide.se
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
